/**
 * What a model's tokens cost at list price, and what kind of work a workflow
 * step is — the two readings the statistics need that a transcript does not say.
 *
 * **Cost is an estimate at API list prices**, in micro-dollars, whatever the
 * session was billed through: a subscription pays nothing per token, and the
 * figure is still the honest way to compare one model's work with another's.
 * Neither CLI writes a cost into its transcript, so it is computed from the
 * tokens it does write. Claude's prices are Anthropic's published per-million
 * rates (cache writes at 1.25× input); Codex's are OpenAI's GPT-5 rates applied
 * to each of its models, which is an approximation and is said to be one where
 * the page shows it.
 */
import { cleanModel, type StepKind } from './day';

export interface Price {
  /** Dollars per million tokens. */
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
}

const p = (input: number, output: number, cacheRead: number, cacheWrite = input * 1.25): Price => ({
  input,
  output,
  cacheRead,
  cacheWrite
});

const FABLE = p(10, 50, 0.25);
const OPUS = p(4, 20, 0.2);
const OPUS_EARLIER = p(5, 25, 0.5);
const SONNET = p(2, 10, 0.2);
const SONNET_4 = p(3, 15, 0.3);
const HAIKU = p(1, 5, 0.1);
const GPT = p(1.25, 10, 0.125, 0);

/**
 * The alias a model id is filed under: Claude's transcripts name a dated id
 * (`claude-opus-4-8`), which the statistics keep as the alias the app launches
 * with (`opus`); Codex's ids are already the aliases.
 */
export function modelAlias(raw: string): string {
  const id = raw.toLowerCase();
  for (const family of ['fable', 'mythos', 'opus', 'sonnet', 'haiku']) {
    if (id.includes(family)) return family === 'mythos' ? 'fable' : family;
  }
  return cleanModel(id);
}

/** The list price of a model, from its full id where there is one — `opus` alone is the current Opus. */
export function priceOf(raw: string): Price {
  const id = raw.toLowerCase();
  if (id.includes('fable') || id.includes('mythos')) return FABLE;
  if (id.includes('opus')) return /opus-4|opus-5(?!-5)/.test(id) ? OPUS_EARLIER : OPUS;
  if (id.includes('sonnet')) return /sonnet-4/.test(id) ? SONNET_4 : SONNET;
  if (id.includes('haiku')) return HAIKU;
  return GPT;
}

/** Whether a model's cost is an approximation rather than its own published price. */
export function priceIsEstimate(model: string): boolean {
  return priceOf(model) === GPT;
}

export interface TokenCounts {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
}

/** What those tokens cost on that model, in micro-dollars. */
export function costMicros(model: string, tokens: TokenCounts): number {
  const price = priceOf(model);
  return Math.round(
    tokens.input * price.input +
      tokens.output * price.output +
      tokens.cacheRead * price.cacheRead +
      tokens.cacheWrite * price.cacheWrite
  );
}

/** The light tier's model for the agent that runs `model` — what a mechanical step could have run on. */
export function lightModelFor(model: string): string {
  return /fable|mythos|opus|sonnet|haiku/.test(model) ? 'haiku' : 'gpt-5.6-luna';
}

/** Whether `model` is a light-tier model already. */
export function isLightModel(model: string): boolean {
  return model === 'haiku' || model === 'gpt-5.6-luna';
}

const JUDGE = /\b(plan|planning|review|design|architect\w*|audit|investigat\w*|debug\w*|analy[sz]\w*|triage|spec)\b/i;
const MECH = /\b(tests?|lint\w*|format\w*|commit\w*|publish\w*|push|checks?|release|deploy\w*|typecheck\w*|ci)\b/i;

/**
 * What kind of work a workflow step is, from its name first and then its
 * instruction: judgement, implementation, or mechanical. A step whose words say
 * nothing either way is implementation — the middle tier, which is also what a
 * step that names no model runs at.
 */
export function stepKind(step: { name: string; instruction: string }): StepKind {
  for (const text of [step.name, step.instruction.slice(0, 240)]) {
    if (JUDGE.test(text)) return 'judge';
    if (MECH.test(text)) return 'mech';
  }
  return 'build';
}
